import { useEffect, useRef, useState } from 'react';

import { extBadgeClasses, fileExt } from './vault-format';
import type { StagedUrl } from './useVault';

export interface UploadModalProps {
  onClose: () => void;
  /** Commits the staged set; resolves when the uploads persisted. */
  onConfirm: (files: File[], urls: StagedUrl[]) => Promise<void>;
}

/** "1.8 MB" / "340 KB" for a staged File (prototype's pending-list size). */
function stagedSize(file: File): string {
  if (file.size > 1_048_576) return `${(file.size / 1_048_576).toFixed(1)} MB`;
  return `${String(Math.max(1, Math.round(file.size / 1024)))} KB`;
}

/** The prototype's addVaultUrl name derivation: last path segment, else host. */
function urlDisplayName(url: string): string {
  const last = url.split('/').pop()?.split('?')[0] ?? '';
  if (last.includes('.')) return last;
  return url.replace(/^https?:\/\//, '').split('/')[0] ?? url;
}

const CLOSE_ICON = (
  <svg
    viewBox="0 0 16 16"
    width="12"
    height="12"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <line x1="4" y1="4" x2="12" y2="12" />
    <line x1="12" y1="4" x2="4" y2="12" />
  </svg>
);

/**
 * The v7 "Upload to Vault" modal (SCRUM-380): File / URL tabs, a dropzone with
 * drag states (click-to-browse + drop), the staged-file list with ext badges,
 * the URL input + staged URL list, and an "Add to Vault" button that stays
 * DISABLED until something is staged. Commit goes through the real API
 * (useVaultUpload) — the caller closes the modal after the mutation resolves.
 */
export function UploadModal({ onClose, onConfirm }: UploadModalProps) {
  const [tab, setTab] = useState<'file' | 'url'>('file');
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [stagedUrls, setStagedUrls] = useState<StagedUrl[]>([]);
  const [urlValue, setUrlValue] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const stagedCount = stagedFiles.length + stagedUrls.length;

  const stageFiles = (list: FileList | File[]) => {
    setStagedFiles((prev) => [...prev, ...Array.from(list)]);
  };

  const addUrl = () => {
    const url = urlValue.trim();
    if (!url) return;
    setStagedUrls((prev) => [...prev, { url, name: urlDisplayName(url) }]);
    setUrlValue('');
  };

  const confirm = () => {
    if (stagedCount === 0 || submitting) return;
    setSubmitting(true);
    setError(null);
    onConfirm(stagedFiles, stagedUrls).catch((e: unknown) => {
      setSubmitting(false);
      setError(e instanceof Error ? e.message : 'Upload failed. Please try again.');
    });
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40">
      <div
        role="dialog"
        aria-label="Upload to Vault"
        className="bg-bg-4 border-border-1 rounded-tk-lg shadow-tk-lg flex w-[520px] max-w-[calc(100vw-32px)] flex-col border"
      >
        {/* Header */}
        <div className="border-border-0 flex items-center justify-between border-b px-5 py-4">
          <span className="font-display text-text-0 text-base font-semibold">Upload to Vault</span>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="text-text-3 hover:bg-bg-3 hover:text-text-0 rounded-tk-sm grid h-[26px] w-[26px] place-items-center"
          >
            {CLOSE_ICON}
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {/* Tab toggle — v7 underline tabs with cyan-dim active fill */}
          <div className="border-border-0 mb-4 flex border-b">
            {(
              [
                { key: 'file', label: 'File upload' },
                { key: 'url', label: 'URL' },
              ] as const
            ).map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => {
                  setTab(t.key);
                }}
                className={`flex items-center gap-1.5 px-4 py-2 text-[0.8125rem] transition-colors ${
                  tab === t.key
                    ? 'text-brand-primary border-brand-primary bg-brand-dim border-b-2 font-semibold'
                    : 'text-text-3 border-b-2 border-transparent'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'file' ? (
            <>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                aria-label="Choose files"
                onChange={(e) => {
                  if (e.target.files) stageFiles(e.target.files);
                  e.target.value = '';
                }}
              />
              <button
                type="button"
                aria-label="Drop files here or click to browse"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  stageFiles(e.dataTransfer.files);
                }}
                className={`rounded-tk-lg mb-4 w-full border-2 border-dashed px-8 py-6 text-center transition-colors ${
                  dragOver ? 'border-brand-primary bg-brand-dim' : 'border-border-1 bg-bg-3'
                }`}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  className="text-text-3 mx-auto mb-2 block"
                  aria-hidden="true"
                >
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                <p className="text-text-2 text-[0.8125rem] font-medium">
                  Drop files here or click to browse
                </p>
              </button>

              {stagedFiles.length > 0 && (
                <ul className="flex flex-col gap-2">
                  {stagedFiles.map((f, i) => (
                    <li
                      key={`${f.name}-${String(i)}`}
                      className="bg-bg-2 border-border-0 rounded-tk-md flex items-center justify-between border px-3 py-2"
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <span
                          className={`rounded-tk-sm inline-block px-1.5 py-px font-mono text-[0.625rem] font-semibold tracking-wide uppercase ${extBadgeClasses(fileExt(f.name))}`}
                        >
                          {fileExt(f.name) || 'file'}
                        </span>
                        <span className="text-text-0 truncate text-[0.8125rem] font-medium">
                          {f.name}
                        </span>
                      </span>
                      <span className="flex shrink-0 items-center gap-3">
                        <span className="text-text-3 font-mono text-[0.6875rem]">
                          {stagedSize(f)}
                        </span>
                        <button
                          type="button"
                          aria-label={`Remove ${f.name}`}
                          onClick={() => {
                            setStagedFiles((prev) => prev.filter((_, j) => j !== i));
                          }}
                          className="text-text-3 hover:text-danger p-0.5 transition-colors"
                        >
                          {CLOSE_ICON}
                        </button>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <>
              <div className="mb-2 flex gap-2">
                <input
                  type="url"
                  value={urlValue}
                  placeholder="https://example.com/document.pdf"
                  aria-label="Document URL"
                  onChange={(e) => {
                    setUrlValue(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addUrl();
                    }
                  }}
                  className="border-border-1 bg-bg-3 text-text-0 rounded-tk-md focus:border-brand-primary flex-1 border px-3 py-2 text-[0.8125rem] outline-none"
                />
                <button
                  type="button"
                  onClick={addUrl}
                  className="border-border-1 bg-bg-3 text-text-1 hover:border-brand-primary hover:text-brand-primary rounded-tk-md shrink-0 border px-4 py-2 text-[0.8125rem] font-medium transition-colors"
                >
                  Add
                </button>
              </div>
              <p className="text-text-3 mb-4 text-xs">Paste a link to a hosted document.</p>

              {stagedUrls.length > 0 && (
                <ul className="flex flex-col gap-2">
                  {stagedUrls.map((u, i) => (
                    <li
                      key={`${u.url}-${String(i)}`}
                      className="bg-bg-2 border-border-0 rounded-tk-md flex items-center justify-between border px-3 py-2"
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <svg
                          viewBox="0 0 16 16"
                          width="14"
                          height="14"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="text-brand-primary shrink-0"
                          aria-hidden="true"
                        >
                          <path d="M6.5 8.5a3 3 0 004.2.4l1.5-1.5a3 3 0 00-4.2-4.2L7 4.2" />
                          <path d="M9.5 7.5a3 3 0 00-4.2-.4L3.8 8.6a3 3 0 004.2 4.2L9 11.8" />
                        </svg>
                        <span
                          className="text-text-0 truncate text-[0.8125rem] font-medium"
                          title={u.url}
                        >
                          {u.name}
                        </span>
                      </span>
                      <button
                        type="button"
                        aria-label={`Remove ${u.name}`}
                        onClick={() => {
                          setStagedUrls((prev) => prev.filter((_, j) => j !== i));
                        }}
                        className="text-text-3 hover:text-danger shrink-0 p-0.5 transition-colors"
                      >
                        {CLOSE_ICON}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}

          {error && <p className="text-danger mt-3 text-xs">{error}</p>}
        </div>

        {/* Footer */}
        <div className="border-border-0 flex items-center justify-between border-t px-5 py-4">
          <span className="text-text-3 font-mono text-[0.6875rem]">
            {stagedCount > 0
              ? `${String(stagedCount)} item${stagedCount === 1 ? '' : 's'} staged`
              : ''}
          </span>
          <span className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="border-border-1 bg-bg-3 text-text-1 hover:text-text-0 rounded-tk-md border px-4 py-2 text-[0.8125rem] font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={stagedCount === 0 || submitting}
              onClick={confirm}
              className="bg-brand-primary text-bg-0 rounded-tk-md px-4 py-2 text-[0.8125rem] font-semibold transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
            >
              {submitting ? 'Adding…' : 'Add to Vault'}
            </button>
          </span>
        </div>
      </div>
    </div>
  );
}
